import { CSSResult, LitElement, PropertyValueMap } from 'lit'
import { property, state } from 'lit/decorators.js'
import { IBaseMixin } from './baseElement'
import { componentText } from './component-text'
import { Constructor } from './constructor'
import { ITailwindElementMixin } from './tailwind.mixin'
import { SchmancyElement } from './SchmancyElement'

/**
 * Cross-realm brand used by `<schmancy-form>` to discover form fields by
 * inheritance rather than tag-name allowlists. `Symbol.for` puts the symbol in
 * the global registry so detection works across module realms/bundles.
 */
export const SCHMANCY_FORM_FIELD = Symbol.for('schmancy.form-field')

/**
 * Composed event a field dispatches in `connectedCallback`. `<schmancy-form>`
 * subscribes via `fromEvent(this, FIELD_CONNECT_EVENT)` and adds the field to
 * its registry. No matching disconnect event — composed events from a
 * disconnected node have an empty composed path; cleanup is via `isConnected`
 * filter at iteration time.
 */
export const FIELD_CONNECT_EVENT = 'schmancy:field:connect'

/**
 * Validation modes.
 * - `dirty` (default per Revolute) — show errors once the value diverged AND
 *   the user finished with the field: blur for a progressively typed value, the
 *   act itself for a value chosen in one discrete gesture. Raising on divergence
 *   alone fires on the first keystroke, telling the user their half-typed email
 *   is malformed while they are still typing it.
 * - `touched` — show errors after first blur, even if value unchanged.
 * - `always` — show errors immediately (live search).
 * - `submitted` — hold all errors until submit (wizard step).
 * - `length` — fire validation only when `value.length` reaches `maxlength`
 *   (Stripe pattern for predictable-length fields: ZIP, phone, card number).
 */
export type ValidateOn = 'always' | 'touched' | 'dirty' | 'submitted' | 'length'

/**
 * Per-field error-message override map. Keys are `ValidityState` flag names;
 * values are the message to display when that flag is set. Unset flags fall
 * back to the `formField.error.*` wording table (`componentText`), which an app
 * localizes once for every field rather than per instance here.
 *
 * Use for i18n or domain-specific copy:
 * ```ts
 * <schmancy-input
 *   .errorMessages=${{ valueMissing: 'Adresse e-mail requise', typeMismatch: 'Format e-mail invalide' }}
 *   type="email" required
 * ></schmancy-input>
 * ```
 */
export type ErrorMessages = Partial<Record<keyof ValidityState, string>>

/**
 * Value shapes a form field can hold. The `readonly object[]` arm is for
 * fields whose value is a list of records rather than of primitives
 * (`schmancy-attachments`). The platform's `setFormValue` accepts no array of
 * any kind, so such a field already owns its own `willUpdate` /
 * `toFormEntries` / `formStateRestoreCallback` overrides; this arm only stops
 * the base type from forcing it to misdescribe what it holds.
 */
export type FormFieldValue = string | string[] | boolean | number | readonly object[] | undefined

/**
 * Interface defining the properties and methods that the FormFieldMixin adds.
 */
export interface IFormFieldMixin extends Element {
	name: string
	value: FormFieldValue
	label: string
	required: boolean
	disabled: boolean
	readonly: boolean
	error: boolean
	validationMessage: string
	hint?: string
	id: string

	/** Set to `true` once the user has blurred the field at least once. */
	touched: boolean
	/** `true` when the current value differs from the captured default. */
	dirty: boolean
	/** Inverse of `dirty`. */
	pristine: boolean
	/** Set to `true` by `<schmancy-form>` on submit (forces error display). */
	submitted: boolean
	/** Validation mode — controls when errors display. Default `'dirty'`. */
	validateOn: ValidateOn

	/**
	 * Per-field error-message override map (i18n hook). See `ErrorMessages`.
	 * Unset keys fall back to the `formField.error.*` wording table.
	 */
	errorMessages?: ErrorMessages

	/**
	 * `ElementInternals` instance attached by the mixin, so a subclass reads
	 * `internals.validity` and sets its form value without an `attachInternals`
	 * call of its own. A validity verdict goes through `setFieldValidity`.
	 */
	internals: ElementInternals | undefined

	form: HTMLFormElement | null

	checkValidity(): boolean
	reportValidity(): boolean
	setCustomValidity(message: string): void

	/**
	 * Writes one validity verdict to both surfaces — the one path every field's
	 * `checkValidity` reports through. `flags` with nothing set is valid;
	 * `anchor` is the platform's own `setValidity` anchor. Platform validity
	 * always gets the truth, and a standing `setCustomValidity` refusal adds
	 * `customError` to whatever else fails. The rendered error is raised only
	 * while `shouldShowError()` is open; an error this method raised follows
	 * every later verdict — its words updated while it stays invalid, cleared as
	 * soon as one is valid — whether or not the gate is open. An `error` or
	 * `validationMessage` written from outside, with or without words, is never
	 * taken over or cleared. A disabled field is valid and drops an error this
	 * method raised. Returns the validity.
	 */
	setFieldValidity(flags: ValidityStateFlags, message?: string, anchor?: HTMLElement): boolean

	/** Mark the field as touched (component should call on blur). */
	markTouched(): void
	/** Mark the field as submitted (called by `<schmancy-form>` on submit). */
	markSubmitted(): void
	/**
	 * Clear the `submitted` flag without resetting value/touched/error.
	 * Used by wizards: stepping back from step N to step N-1 should not
	 * leave step N-1's fields in aggressive "show all errors" mode.
	 */
	clearSubmitted(): void

	/**
	 * `true` while an async validator is in flight. Broadcast as
	 * `:state(validating)`. `<schmancy-form>` blocks submit until every
	 * registered field's `isValidating` is back to `false`.
	 */
	isValidating: boolean

	/**
	 * Run an async validator. While the promise is pending, `isValidating` is
	 * `true` and `:state(validating)` is broadcast. On resolve, the returned
	 * string is passed to `setCustomValidity` — empty string clears any
	 * existing custom error; non-empty marks the field invalid with that
	 * message.
	 *
	 * Submitting the form while a validator is pending is queued — the form
	 * waits for `Promise.all(pending validators)` to settle before proceeding.
	 */
	runAsyncValidator(fn: () => Promise<string>): Promise<void>

	/**
	 * Whether the gate for showing validation errors is open right now. Exposed
	 * so subclasses with custom error-display channels (e.g. select renders
	 * errors on a child input) can keep their gate consistent with the mixin's.
	 * Subclasses should not override this — extend the truth table by changing
	 * `validateOn` instead.
	 */
	shouldShowError(): boolean

	toFormEntries(): Array<[string, FormDataEntryValue]>
	resetForm(): void

	/**
	 * `true` once `setCustomValidity(message)` has been called with a non-empty
	 * `message` and no subsequent call with `''`/`resetForm()` has cleared it.
	 * This is the field's own tracked intent — the sole authority a subclass's
	 * `checkValidity()` override should consult to decide whether to preserve a
	 * custom error across a routine re-validation pass. Reading
	 * `internals.validity.customError` back from the platform for that decision
	 * is the bug this method exists to prevent: WebKit's `ElementInternals`
	 * validity state is form-associated-custom-element-and-autofill-adjacent
	 * platform state this component does not exclusively own, so a flag that
	 * leaked or lagged true there (observed on iOS Safari) could never be
	 * cleared again once nothing but "is it currently true" gated the clear.
	 */
	hasExplicitCustomError(): boolean

	/**
	 * Native FACE lifecycle — restore value after bfcache / form autofill.
	 * Declared here (not just implemented on the mixin class) so a subclass
	 * whose value model isn't a flat string — e.g. date-range's two-part
	 * `start`/`end`, submitted as a multi-entry `FormData` — can `override` it.
	 */
	formStateRestoreCallback(state: string | File | FormData | null): void

	emitChange(detail: any): void
}

/**
 * The auto-id sequence lives on `globalThis` under a registered symbol rather
 * than in module scope: one document can execute two copies of this module (one
 * consumer resolving the `source` export condition, another `dist/mixins.js`),
 * and two module-scope counters would both hand out `schmancy-field-1`. Boxed so
 * the increment writes back to the shared slot.
 */
const FIELD_SEQUENCE = Symbol.for('schmancy.form-field.sequence')
const fieldSequence = ((globalThis as { [FIELD_SEQUENCE]?: { n: number } })[FIELD_SEQUENCE] ??= { n: 0 })

/** Predicate used by `<schmancy-form>` to detect mixin descendants. */
export function isSchmancyFormField(el: unknown): el is IFormFieldMixin {
	return !!el && typeof el === 'object' && (el as any).constructor?.[SCHMANCY_FORM_FIELD] === true
}

/**
 * A mixin that adds form field capabilities to a LitElement class.
 * Components that extend this mixin are automatically discovered and
 * collected by `<schmancy-form>` — no tag-name registration needed.
 *
 * Subclasses may override `toFormEntries()` to contribute multiple
 * name/value pairs to FormData (e.g. date-range, tag-input).
 *
 * @example
 * ```ts
 * class MyInput extends FormFieldMixin(TailwindElement(css`...`)) {
 *   // Your component code here
 * }
 * ```
 */
export function FormFieldMixin<T extends Constructor<LitElement>>(superClass: T) {
	class FormFieldMixinClass extends superClass {
		static formAssociated = true

		/** Brand for cross-realm detection by `<schmancy-form>`. */
		static readonly [SCHMANCY_FORM_FIELD] = true

		// Element internals for form association
		internals: ElementInternals | undefined

		/** Value snapshot captured at first render, used by `resetForm()`. */
		protected defaultValue: FormFieldValue = undefined

		/** Whether the error on screen is one `setFieldValidity` raised — the only
		 *  kind a verdict clears. Any outside write of `error` or
		 *  `validationMessage` hands it back, so a consumer's own error stands. */
		private ownsError = false

		/** The standing `setCustomValidity` message, held apart from the platform's
		 *  `customError` flag because a field raises that flag for verdicts of its
		 *  own (an end before its start) that must not outlive their cause. */
		private refusal = ''

		private errorShown = false

		private messageShown = ''

		hasExplicitCustomError(): boolean {
			return this.refusal !== ''
		}

		@property({ type: String })
		name: string = ''

		@property({ reflect: true })
		value: FormFieldValue = ''

		@property({ type: String })
		label: string = ''

		@property({ type: Boolean, reflect: true })
		required: boolean = false

		@property({ type: Boolean, reflect: true })
		disabled: boolean = false

		@property({ type: Boolean, reflect: true })
		readonly: boolean = false

		@property({ type: Boolean, reflect: true })
		get error(): boolean {
			return this.errorShown
		}
		set error(shown: boolean) {
			this.errorShown = shown
			this.ownsError = false
		}

		@property({ type: String })
		get validationMessage(): string {
			return this.messageShown
		}
		set validationMessage(message: string) {
			this.messageShown = message
			this.ownsError = false
		}

		@property({ type: String })
		hint?: string

		// A sequence, not a timestamp plus a random: fields built within one
		// millisecond drew from 1000 buckets and collided, and a light-DOM id lookup
		// resolves the first match — `<schmancy-form-summary>` would focus the wrong
		// field for an error.
		@property({ reflect: true })
		override id: string = `schmancy-field-${++fieldSequence.n}`

		// ----- Validation UX state (Revolute contract) -----

		/** True after the user has blurred the field at least once. */
		@state() touched: boolean = false

		/** Set by `<schmancy-form>` on submit; persists until `resetForm()`. */
		@state() submitted: boolean = false

		/**
		 * When errors display. Default `'dirty'` — an error waits for the value to
		 * diverge from its default AND for the user to be done with the field
		 * (`touched`), so a pristine field never shows an error until submit and a
		 * field being typed into never shows one mid-word. A field whose value is
		 * chosen in one discrete act marks itself touched on that act, so it still
		 * raises as soon as the choice is made.
		 */
		@property({ type: String, reflect: true })
		validateOn: ValidateOn = 'dirty'

		/**
		 * Optional override for the validity-flag → message map. See
		 * `ErrorMessages`. Unset keys fall back to the mixin's defaults.
		 */
		@property({ attribute: false })
		errorMessages?: ErrorMessages

		/** True when value has been changed from the captured default. */
		get dirty(): boolean {
			return this.defaultValue !== undefined && this.value !== this.defaultValue
		}

		get pristine(): boolean {
			return !this.dirty
		}

		markTouched(): void {
			if (!this.touched) this.touched = true
		}

		markSubmitted(): void {
			if (!this.submitted) this.submitted = true
		}

		clearSubmitted(): void {
			if (this.submitted) this.submitted = false
		}

		/**
		 * Reactive flag set while an async validator is in flight. Read by
		 * `<schmancy-form>` to gate submit; broadcast as `:state(validating)`
		 * for CSS spinner targeting.
		 */
		@state() isValidating: boolean = false

		async runAsyncValidator(fn: () => Promise<string>): Promise<void> {
			if (this.isValidating) {
				// Cancel-by-supersede semantics — a new call wins; the previous
				// is best-effort and its result is dropped if it resolves later.
				this.asyncValidatorEpoch++
			}
			const epoch = ++this.asyncValidatorEpoch
			this.isValidating = true
			try {
				const message = await fn()
				if (epoch !== this.asyncValidatorEpoch) return // superseded
				this.setCustomValidity(message)
			} finally {
				if (epoch === this.asyncValidatorEpoch) {
					this.isValidating = false
				}
			}
		}

		private asyncValidatorEpoch: number = 0

		/**
		 * Whether a validity verdict may raise its error on screen right now. It
		 * governs raising only: an error `setFieldValidity` raised clears whatever
		 * this returns. `submitted` overrides every mode — once a submit attempt
		 * happens, the field stays in live-correction mode (Phase 3) for the rest
		 * of the session, and `resetForm()` is the only way back. Subclasses may
		 * call it but should not override it (extend the truth table by changing
		 * `validateOn` instead).
		 */
		shouldShowError(): boolean {
			if (this.submitted) return true
			switch (this.validateOn) {
				case 'always':
					return true
				case 'touched':
					return this.touched
				case 'dirty':
					// The conjunction IS the mode: the value diverged AND the user
					// finished with the field. `dirty` alone is true from the first
					// keystroke, so an email field raised "missing an '@'" at `m`.
					// Every field marks itself touched when its own entry finishes —
					// blur for progressive typing, the act itself for a discrete
					// selection — which is what keeps a chip or a radio raising the
					// moment it is pressed while a textarea waits for the blur.
					return this.dirty && this.touched
				case 'submitted':
					return false
				case 'length':
					// Predictable-length fields: validate once value reaches the
					// maxlength constraint. Stripe pattern. This mode is the opt-in
					// to live validation, so with no maxlength to reach it falls back
					// to divergence ALONE — deliberately not `'dirty'`'s conjunction,
					// which would make a field asking for live validation wait for a
					// blur. A consumer who wants that wait asks for `'dirty'`.
					if (typeof this.value === 'string') {
						const max = (this as unknown as { maxlength?: number }).maxlength
						if (typeof max === 'number' && max > 0) {
							return this.value.length >= max
						}
					}
					return this.dirty
			}
		}

		constructor(...args: any[]) {
			super(...args)
			try {
				this.internals = this.attachInternals()
			} catch {
				this.internals = undefined
			}
			// Recorded as changed for the first update, the way an initialised
			// reactive field is, so `ariaInvalid` and `:state(invalid)` start written.
			this.requestUpdate('error')
			this.requestUpdate('validationMessage')
		}

		override connectedCallback(): void {
			super.connectedCallback()
			// Register with any ancestor <schmancy-form> via composed event.
			this.dispatchEvent(
				new CustomEvent(FIELD_CONNECT_EVENT, {
					detail: this,
					bubbles: true,
					composed: true,
				}),
			)
		}

		/** The form this element is associated with (native FACE behavior). */
		get form(): HTMLFormElement | null {
			return this.internals?.form ?? null
		}

		protected firstUpdated(changedProps: PropertyValueMap<any>): void {
			super.firstUpdated?.(changedProps)
			if (this.defaultValue === undefined) this.defaultValue = this.value
			// Sync platform validity (`internals.setValidity`) once after first
			// render so `<form>.checkValidity()` and `:invalid` reflect truth
			// even when no property change has fired through `willUpdate` yet
			// (e.g. for required-empty fields with class-field-initialized
			// defaults that Lit doesn't see as "changed" on the first cycle).
			this.checkValidity()
		}

		protected willUpdate(changedProps: PropertyValueMap<any>): void {
			super.willUpdate(changedProps)

			// `id=""` in markup, or the attribute removed after upgrade, would leave
			// `<label for>` and every id a field derives from its own pointing at nothing.
			if (!this.id) this.id = `schmancy-field-${++fieldSequence.n}`

			if (changedProps.has('value')) {
				this.internals?.setFormValue(this.value as string | File | FormData | null)
				// :state(dirty) tracks value-vs-default; recompute on every value change.
				if (this.dirty) this.internals?.states.add('dirty')
				else this.internals?.states.delete('dirty')
				// Always sync platform validity (internals.setValidity) so
				// `<form>.checkValidity()` and `:invalid` reflect truth, even
				// while the visual `error` gate stays closed for pristine
				// fields under `validateOn: 'dirty'`. The visibility gate is
				// inside setFieldValidity() itself.
				this.checkValidity()
			}

			if (changedProps.has('required') || changedProps.has('disabled')) {
				this.checkValidity()
			}

			// An error this field raised is re-derived on every update, which is what
			// makes its message follow a `setComponentText` swap: the wording read
			// lives inside checkValidity, so an update where checkValidity doesn't run
			// drops that dependency and strands the message in the outgoing language.
			if (this.ownsError) {
				this.checkValidity()
			}

			if (changedProps.has('isValidating')) {
				if (this.isValidating) this.internals?.states.add('validating')
				else this.internals?.states.delete('validating')
			}

			if (changedProps.has('touched')) {
				if (this.touched) this.internals?.states.add('touched')
				else this.internals?.states.delete('touched')
				// Phase 2 — blur on a dirty field. Validate to surface the error.
				if (this.touched && this.dirty) this.checkValidity()
			}

			if (changedProps.has('submitted')) {
				if (this.submitted) {
					this.internals?.states.add('submitted')
					// Phase 4 — submit forces error display regardless of dirty/touched.
					this.checkValidity()
				} else {
					this.internals?.states.delete('submitted')
				}
			}

			// Note: we do not write to `internals.setValidity` from willUpdate
			// based on `error` / `validationMessage` changes. `setFieldValidity`
			// and `setCustomValidity` are the sole writers — overwriting the
			// validity flags from willUpdate would clobber structured flags
			// set by subclass overrides (e.g. input.ts surfaces typeMismatch /
			// patternMismatch / tooShort / etc.).

			if (changedProps.has('error')) {
				if (this.error) this.internals?.states.add('invalid')
				else this.internals?.states.delete('invalid')
				// ARIA reflection through ElementInternals reaches AT through shadow DOM.
				if (this.internals) this.internals.ariaInvalid = this.error ? 'true' : 'false'
			}

			if (changedProps.has('required') || changedProps.has('disabled')) {
				if (this.required) this.internals?.states.add('required')
				else this.internals?.states.delete('required')
				// Suppress aria-required when disabled — disabled fields shouldn't
				// announce as required to assistive tech.
				if (this.internals) {
					this.internals.ariaRequired = this.required && !this.disabled ? 'true' : 'false'
				}
			}
			if (changedProps.has('disabled')) {
				if (this.disabled) this.internals?.states.add('disabled')
				else this.internals?.states.delete('disabled')
			}
			if (changedProps.has('readonly')) {
				if (this.readonly) this.internals?.states.add('readonly')
				else this.internals?.states.delete('readonly')
			}
		}

		/**
		 * Native FACE lifecycle — called by the browser when the owning form
		 * is reset. Delegates to `resetForm()` so subclasses have one
		 * override point for both programmatic and user-initiated resets.
		 */
		formResetCallback(): void {
			this.resetForm()
		}

		/** Native FACE lifecycle — called when the form's disabled state changes. */
		formDisabledCallback(disabled: boolean): void {
			this.disabled = disabled
		}

		/**
		 * Native FACE lifecycle — restore value after bfcache / form autofill.
		 */
		formStateRestoreCallback(state: string | File | FormData | null): void {
			if (state == null) return
			this.value = state as any
		}

		/** Override to customize reset behavior; default restores `defaultValue`. */
		resetForm(): void {
			this.value = this.defaultValue ?? ''
			this.error = false
			this.validationMessage = ''
			this.refusal = ''
			this.touched = false
			this.submitted = false
			this.internals?.setValidity({})
		}

		/**
		 * Contribute entries to a parent FormData. Default: a single
		 * `[name, value]` pair when `name` is set and value is meaningful.
		 * Override for multi-entry controls (e.g. date range).
		 */
		toFormEntries(): Array<[string, FormDataEntryValue]> {
			if (!this.name || this.disabled) return []
			const v = this.value
			if (v === undefined || v === null || v === '') return []
			if (Array.isArray(v)) return v.map(item => [this.name, String(item)] as [string, FormDataEntryValue])
			if (typeof v === 'boolean') return v ? [[this.name, 'on']] : []
			return [[this.name, String(v)]]
		}

		checkValidity(): boolean {
			if (this.required && (this.value === '' || this.value === undefined || this.value === null)) {
				// The `errorMessages` prop is the per-instance override and still wins;
				// `componentText` is only what it falls back to.
				return this.setFieldValidity(
					{ valueMissing: true },
					this.errorMessages?.valueMissing ?? componentText('formField.error.valueMissing'),
				)
			}
			return this.setFieldValidity({})
		}

		setFieldValidity(flags: ValidityStateFlags, message = '', anchor?: HTMLElement): boolean {
			if (this.disabled) {
				// Barred from constraint validation, as a disabled native control is.
				this.internals?.setValidity({})
				if (this.ownsError) {
					this.error = false
					this.validationMessage = ''
				}
				return true
			}
			// A refusal stands beside whatever else fails, as native validity keeps `customError`.
			const verdict: ValidityStateFlags = this.refusal ? { ...flags, customError: true } : flags
			const valid = !Object.values(verdict).some(Boolean)
			// An error or words on screen this method did not write — a refusal, an async
			// validator's finding, a consumer's own `error` — stay exactly as they are.
			const foreign = !this.ownsError && (this.error || this.validationMessage !== '')
			const words = (foreign && this.validationMessage) || this.refusal || message
			// Platform validity is set to the verdict, or unconditionally cleared when
			// valid — never a read-back of `internals.validity.customError` to decide
			// whether to preserve it, which is what leaves a leaked/stale platform flag
			// stuck forever once nothing clears it (the iOS Safari self-heal failure
			// `refusal` exists to avoid: this method tracks its own explicit intent).
			this.internals?.setValidity(valid ? {} : verdict, valid ? undefined : words, anchor)
			if (valid) {
				if (this.ownsError) {
					this.error = false
					this.validationMessage = ''
				}
			} else if (this.ownsError || (!foreign && this.shouldShowError())) {
				this.error = true
				this.validationMessage = words
				this.ownsError = true
			} else if (foreign && !this.error && this.shouldShowError()) {
				this.error = true
			}
			return valid
		}

		reportValidity(): boolean {
			const isValid = this.checkValidity()
			if (!isValid) this.internals?.reportValidity()
			return isValid
		}

		setCustomValidity(message: string): void {
			this.refusal = message
			this.validationMessage = message
			this.error = message !== ''
			if (message) {
				this.internals?.setValidity({ customError: true }, message)
			} else {
				this.internals?.setValidity({})
			}
		}

		emitChange(detail: any): void {
			if ('dispatchScopedEvent' in this && typeof this.dispatchScopedEvent === 'function') {
				this.dispatchScopedEvent('change', detail, { bubbles: true })
			} else {
				this.dispatchEvent(
					new CustomEvent('change', {
						detail,
						bubbles: true,
						composed: true,
					}),
				)
			}
		}
	}

	return FormFieldMixinClass as Constructor<IFormFieldMixin> & T
}

/**
 * A convenience function that composes FormFieldMixin with SchmancyElement
 * to create a base class for Schmancy form components. Subclasses gain the
 * full SchmancyElement stack (SignalWatcher, _activeHost wrap, AbortSignal,
 * automatic Tailwind injection) plus the form-field semantics.
 */
export function SchmancyFormField<T extends CSSResult>(componentStyle?: T) {
	// The mixin call sits in the `extends` clause, not around the returned class:
	// a static analyzer resolving this factory takes the first class-like child of
	// the body as the whole answer, so a `FormFieldMixin(...)` applied after that
	// class is never followed and every property this mixin declares reads as
	// unknown at every consumer's binding.
	class StyledSchmancyElement extends FormFieldMixin(SchmancyElement) {
		static styles = componentStyle ? [componentStyle] : []
	}
	return StyledSchmancyElement as unknown as Constructor<IFormFieldMixin> &
		Constructor<ITailwindElementMixin> &
		Constructor<LitElement> &
		Constructor<IBaseMixin>
}
